import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { AiOutlineClose } from 'react-icons/ai';
import axios from 'axios';
import { URL } from '../AxiosUrlGlobal';
import styles from './SupportModal.module.css';

function SupportModal({ onClose }) {
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let sanitizedValue = value;

    // Sanitize based on field type
    if (name === 'mobile') {
      // Only allow digits, max 11
      sanitizedValue = value.replace(/\D/g, '').slice(0, 11);
    } else if (name === 'name') {
      // Remove dangerous characters, max 100 chars
      sanitizedValue = value.replace(/[<>{}]/g, '').slice(0, 100);
    } else if (name === 'message') {
      // Remove script tags and dangerous patterns, max 1000 chars
      sanitizedValue = value.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/[<>{}]/g, '')
        .slice(0, 1000);
    }

    setFormData({
      ...formData,
      [name]: sanitizedValue
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Trim all values
    const trimmedName = formData.name.trim();
    const trimmedMobile = formData.mobile.trim();
    const trimmedMessage = formData.message.trim();

    // Validation
    if (!trimmedName || !trimmedMobile || !trimmedMessage) {
      toast.error('Please fill in all fields');
      return;
    }

    // Name validation
    if (trimmedName.length < 2) {
      toast.error('Name must be at least 2 characters');
      return;
    }

    if (trimmedName.length > 100) {
      toast.error('Name must not exceed 100 characters');
      return;
    }

    // Check for valid name (letters, spaces, dots, hyphens only)
    if (!/^[a-zA-Z\s.-]+$/.test(trimmedName)) {
      toast.error('Name can only contain letters, spaces, dots, and hyphens');
      return;
    }

    // Mobile validation
    if (trimmedMobile.length !== 11) {
      toast.error('Mobile number must be 11 digits');
      return;
    }

    if (!trimmedMobile.startsWith('01')) {
      toast.error('Mobile number must start with 01');
      return;
    }

    if (!/^\d{11}$/.test(trimmedMobile)) {
      toast.error('Mobile number must contain only digits');
      return;
    }

    // Message validation
    if (trimmedMessage.length < 10) {
      toast.error('Message must be at least 10 characters long');
      return;
    }

    if (trimmedMessage.length > 1000) {
      toast.error('Message must not exceed 1000 characters');
      return;
    }

    // Check for suspicious patterns (basic XSS/injection prevention)
    const dangerousPatterns = [
      /<script/i,
      /javascript:/i,
      /on\w+\s*=/i,
      /<iframe/i,
      /eval\(/i,
      /expression\(/i
    ];

    if (dangerousPatterns.some(pattern => pattern.test(trimmedMessage))) {
      toast.error('Message contains invalid characters');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const headers = {};

      // Add token to headers if available (for authenticated users)
      if (token) {
        headers.token = token;
      }

      const response = await axios.post(
        `${URL}support-ticket`,
        {
          name: trimmedName,
          mobile: trimmedMobile,
          message: trimmedMessage
        },
        { headers }
      );

      if (response.data.success) {
        toast.success('Support ticket submitted successfully! We will contact you soon.');
        setFormData({ name: '', mobile: '', message: '' });
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        toast.error(response.data.error || 'Failed to submit ticket');
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to submit ticket. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>Contact Support</h2>
          <AiOutlineClose className={styles.closeIcon} onClick={onClose} />
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label>Your Name * (max 100 characters)</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your name"
              maxLength="100"
              required
            />
            <small style={{ color: '#6b7280', fontSize: '12px' }}>
              {formData.name.length}/100 characters
            </small>
          </div>

          <div className={styles.formGroup}>
            <label>Mobile Number *</label>
            <input
              type="tel"
              name="mobile"
              value={formData.mobile}
              onChange={handleChange}
              placeholder="01XXXXXXXXX"
              maxLength="11"
              pattern="01[0-9]{9}"
              required
            />
            <small style={{ color: '#6b7280', fontSize: '12px' }}>
              {formData.mobile.length}/11 digits
            </small>
          </div>

          <div className={styles.formGroup}>
            <label>Message * (10-1000 characters)</label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Describe your issue or question..."
              rows="5"
              minLength="10"
              maxLength="1000"
              required
            />
            <small style={{ color: '#6b7280', fontSize: '12px' }}>
              {formData.message.length}/1000 characters
            </small>
          </div>

          <div className={styles.buttonGroup}>
            <button type="button" className={styles.cancelButton} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className={styles.submitButton} disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Ticket'}
            </button>
          </div>
        </form>

        <div className={styles.helpText}>
          <p>Our support team will contact you within 24 hours</p>
        </div>
      </div>
    </div>
  );
}

export default SupportModal;
